import React, { useCallback, useEffect, useMemo, useState } from "react";

const FILTERS = [
  ["pending", "Bekleyenler"],
  ["approved", "Onaylananlar"],
  ["rejected", "Reddedilenler"],
  ["all", "Tümü"],
];

const PANEL_ORIGIN = "https://panel.haymestasarim.com";

const COLORS = {
  navy: "#102a43",
  blue: "#1463b8",
  orange: "#f28c28",
  green: "#168a4a",
  red: "#b91c1c",
  muted: "#627d98",
  border: "#d9e2ec",
  surface: "#f4f7fc",
};

async function readPayload(response) {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { message: raw };
  }
}

function listFrom(payload) {
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function mediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/uploads/")) return `${PANEL_ORIGIN}${raw}`;
  return `${PANEL_ORIGIN}/${raw.replace(/^\/+/, "")}`;
}

function numberValue(value) {
  return new Intl.NumberFormat("tr-TR").format(
    Number(value || 0)
  );
}

function listValue(value, fallback = "Tümü") {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => String(item || "").trim())
      .filter(Boolean);

    return items.length > 0
      ? items.join(", ")
      : fallback;
  }

  const raw = String(value || "").trim();

  if (!raw) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed)) {
      const items = parsed
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      return items.length > 0
        ? items.join(", ")
        : fallback;
    }
  } catch (_) {
    // Normal metin olarak kullanılmaya devam edilir.
  }

  return raw;
}

function targetTypeText(value) {
  const labels = {
    all: "Herkes",
    institution: "Belirli kurum",
    country: "Belirli ülke",
    city: "Belirli şehir",
    district: "Belirli ilçe",
    group: "Belirli grup",
    new_users: "Yeni kullanıcılar",
  };

  return labels[value] || value || "Herkes";
}

function fileSizeValue(value) {
  const bytes = Number(value || 0);

  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "—";
  }

  const megabytes = bytes / (1024 * 1024);

  return `${megabytes.toLocaleString("tr-TR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MB`;
}

function dateValue(value) {

  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function statusText(status) {
  if (status === "approved") return "Onaylandı";
  if (status === "rejected") return "Reddedildi";
  if (status === "cancelled") return "İptal Edildi";
  return "İnceleme Bekliyor";
}

function statusStyle(status) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
  };
  if (status === "approved") {
    return { ...base, color: "#166534", background: "#dcfce7" };
  }
  if (status === "rejected") {
    return { ...base, color: "#991b1b", background: "#fee2e2" };
  }
  if (status === "cancelled") {
    return { ...base, color: "#475569", background: "#e2e8f0" };
  }
  return { ...base, color: "#92400e", background: "#fef3c7" };
}

function Detail({ label, value }) {
  return (
    <div
      style={{
        padding: "10px 11px",
        borderRadius: 12,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
      }}
    >
      <span
        style={{
          display: "block",
          color: COLORS.muted,
          fontSize: 10,
          fontWeight: 800,
        }}
      >
        {label}
      </span>
      <b
        style={{
          display: "block",
          marginTop: 4,
          color: COLORS.navy,
          fontSize: 13,
          overflowWrap: "anywhere",
        }}
      >
        {value || "—"}
      </b>
    </div>
  );
}

export default function MespuanReklamBasvurulari({
  API,
  token,
  setMessage,
}) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [openById, setOpenById] = useState({});
  const [notes, setNotes] = useState({});

  const fetchApplications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filter !== "all") params.set("status", filter);

      const response = await fetch(
        `${API}/mespuan/reklam/admin?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      const payload = await readPayload(response);
      if (!response.ok) {
        throw new Error(
          payload?.message || "Reklam başvuruları alınamadı"
        );
      }
      setItems(listFrom(payload));
    } catch (error) {
      setItems([]);
      setMessage?.({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }, [API, filter, setMessage, token]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const visibleItems = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr-TR");
    if (!term) return items;


    return items.filter((item) =>
      [
        item.title,
        item.sponsor_name,
        item.institution_name,
        item.institution_id,
        item.description,
        item.ad_category,
        item.target_type,
        item.target_value,
        listValue(item.target_genders, ""),
        listValue(item.target_sectors, ""),
        listValue(item.target_languages, ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr-TR")
        .includes(term)
    );


  }, [items, search]);

  async function decide(item, decision) {
    const note = String(
      notes[item.id] || ""
    ).trim();

    if (
      decision === "approve" &&
      item.consent_confirmed !== true
    ) {
      setMessage?.({
        type: "error",
        text:
          "Bu başvuruda yayın hakkı ve reklam kuralları onayı bulunmuyor. Başvuru onaylanamaz.",
      });

      return;
    }

    if (
      decision === "approve" &&
      Number(item.view_credit_cost || 0) <= 0 &&
      Number(item.click_credit_cost || 0) <= 0
    ) {
      const continueWithoutCredit = window.confirm(
        "Bu kampanyanın izleme ve tıklama kredi bedeli 0 görünüyor. Reklam ücretsiz yayınlanacak. Yine de onaylamak istiyor musunuz?"
      );

      if (!continueWithoutCredit) {
        return;
      }
    }

    if (
      decision === "reject" &&
      note.length < 5
    ) {
      setMessage?.({
        type: "error",
        text:
          "Reddetmek için en az 5 karakterlik gerekçe yazmalısınız.",
      });

      return;
    }

    const verb = decision === "approve" ? "onaylamak" : "reddetmek";
    if (
      !window.confirm(
        `“${item.title}” reklam başvurusunu ${verb} istiyor musunuz?`
      )
    ) {
      return;
    }

    setActionId(item.id);
    try {
      const response = await fetch(
        `${API}/mespuan/reklam/admin/${item.id}/${decision}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note: note || null }),
        }
      );
      const payload = await readPayload(response);
      if (!response.ok) {
        throw new Error(payload?.message || "İşlem tamamlanamadı");
      }
      setMessage?.({
        type: "success",
        text:
          payload?.message ||
          (decision === "approve"
            ? "Reklam onaylandı ve yayınlandı."
            : "Reklam başvurusu reddedildi."),
      });
      setNotes((previous) => ({ ...previous, [item.id]: "" }));
      await fetchApplications();
    } catch (error) {
      setMessage?.({ type: "error", text: error.message });
    } finally {
      setActionId(null);
    }
  }

  return (
    <section style={{ marginTop: 26 }}>
      <div
        style={{
          padding: 22,
          borderRadius: 24,
          background: "#fff",
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 18px 45px rgba(16,42,67,.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                color: COLORS.orange,
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: ".09em",
              }}
            >
              MESYAT REKLAM DOĞRULAMA MERKEZİ
            </div>
            <h2
              style={{
                margin: "6px 0 5px",
                color: COLORS.navy,
                fontSize: 25,
              }}
            >
              Sponsor Video Başvuruları
            </h2>
            <p
              style={{
                margin: 0,
                color: COLORS.muted,
                lineHeight: 1.5,
              }}
            >
              Kurumların yüklediği reklam videolarını izleyin; MesPuan
              bütçesini kontrol ederek onaylayın veya gerekçesiyle reddedin.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchApplications}
            disabled={loading}
            style={{
              alignSelf: "flex-start",
              padding: "10px 14px",
              borderRadius: 12,
              border: 0,
              color: "#fff",
              background: COLORS.blue,
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            {loading ? "Yükleniyor..." : "Yenile"}
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 18,
            flexWrap: "wrap",
          }}
        >
          {FILTERS.map(([value, label]) => (
            <button
              type="button"
              key={value}
              onClick={() => setFilter(value)}
              style={{
                padding: "9px 12px",
                borderRadius: 999,
                border:
                  filter === value
                    ? `1px solid ${COLORS.blue}`
                    : `1px solid ${COLORS.border}`,
                color: filter === value ? "#fff" : COLORS.navy,
                background: filter === value ? COLORS.blue : "#fff",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Kurum, sponsor veya reklam ara..."
            style={{
              flex: "1 1 260px",
              minHeight: 40,
              padding: "0 12px",
              borderRadius: 12,
              border: `1px solid ${COLORS.border}`,
            }}
          />
        </div>

        <div style={{ display: "grid", gap: 13, marginTop: 18 }}>
          {!loading && visibleItems.length === 0 && (
            <div
              style={{
                padding: 30,
                textAlign: "center",
                borderRadius: 18,
                color: COLORS.muted,
                background: COLORS.surface,
              }}
            >
              <b
                style={{
                  display: "block",
                  color: COLORS.navy,
                  marginBottom: 5,
                }}
              >
                Bu görünümde reklam başvurusu yok
              </b>
              Yeni sponsor video başvuruları geldiğinde burada görünecek.
            </div>
          )}

          {visibleItems.map((item) => {
            const open = Boolean(openById[item.id]);
            const busy = actionId === item.id;
            return (
              <article
                key={item.id}
                style={{
                  overflow: "hidden",
                  borderRadius: 18,
                  border: `1px solid ${COLORS.border}`,
                  background: "#fff",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenById((previous) => ({
                      ...previous,
                      [item.id]: !previous[item.id],
                    }))
                  }
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    alignItems: "center",
                    padding: 15,
                    border: 0,
                    background: "#fff",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <b
                      style={{
                        display: "block",
                        color: COLORS.navy,
                        fontSize: 16,
                      }}
                    >
                      {item.title || "İsimsiz reklam"}
                    </b>
                    <span
                      style={{
                        display: "block",
                        color: COLORS.muted,
                        fontSize: 12,
                        marginTop: 4,
                      }}
                    >


                      {item.institution_name ||
                        `Kurum #${item.institution_id}`}{" "}
                      · {item.sponsor_name ||
                        "Sponsor belirtilmedi"}{" "}
                      · {item.ad_category ||
                        "general"}{" "}
                      · {targetTypeText(
                        item.target_type
                      )}{" "}
                      · {dateValue(item.created_at)}


                    </span>
                  </span>
                  <span style={statusStyle(item.status)}>
                    {statusText(item.status)}
                  </span>
                </button>

                {open && (
                  <div
                    style={{
                      padding: 15,
                      borderTop: `1px solid ${COLORS.border}`,
                      background: "#fbfdff",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit,minmax(260px,1fr))",
                        gap: 14,
                      }}
                    >
                      <div>
                        <video
                          controls
                          preload="metadata"
                          poster={mediaUrl(item.thumbnail_url) || undefined}
                          src={mediaUrl(item.video_url)}
                          style={{
                            display: "block",
                            width: "100%",
                            maxHeight: 330,
                            borderRadius: 16,
                            background: "#071827",
                          }}
                        >
                          Tarayıcınız video oynatmayı desteklemiyor.
                        </video>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit,minmax(145px,1fr))",
                          gap: 9,
                          alignContent: "start",
                        }}
                      >



                        <Detail
                          label="Kurum"
                          value={
                            item.institution_name ||
                            `Kurum #${item.institution_id}`
                          }
                        />

                        <Detail
                          label="Sponsor"
                          value={
                            item.sponsor_name ||
                            "Sponsor belirtilmedi"
                          }
                        />

                        <Detail
                          label="Reklam kategorisi"
                          value={
                            item.ad_category ||
                            "general"
                          }
                        />

                        <Detail
                          label="MesPuan ödülü"
                          value={`${numberValue(
                            item.reward_points
                          )} MP`}
                        />

                        <Detail
                          label="Toplam MesPuan bütçesi"
                          value={
                            item.budget_points
                              ? `${numberValue(
                                  item.budget_points
                                )} MP`
                              : "Sınırsız"
                          }
                        />

                        <Detail
                          label="Video süresi"
                          value={`${
                            item.duration_seconds || 0
                          } saniye`}
                        />

                        <Detail
                          label="Video dosya boyutu"
                          value={fileSizeValue(
                            item.video_size_bytes
                          )}
                        />

                        <Detail
                          label="Günlük izleme"
                          value={
                            item.daily_view_limit || 1
                          }
                        />

                        <Detail
                          label="Toplam izlenme sınırı"
                          value={
                            item.total_view_limit ||
                            "Sınırsız"
                          }
                        />

                        <Detail
                          label="Tamamlama oranı"
                          value={`%${
                            item.completion_percent ||
                            100
                          }`}
                        />

                        <Detail
                          label="Hedef kitle"
                          value={targetTypeText(
                            item.target_type
                          )}
                        />

                        <Detail
                          label="Hedef değer"
                          value={
                            item.target_value ||
                            "Tüm kullanıcılar"
                          }
                        />

                        <Detail
                          label="Yaş aralığı"
                          value={
                            item.target_min_age ||
                            item.target_max_age
                              ? `${
                                  item.target_min_age ||
                                  13
                                } - ${
                                  item.target_max_age ||
                                  100
                                }`
                              : "Tüm yaşlar"
                          }
                        />

                        <Detail
                          label="Cinsiyet hedefi"
                          value={listValue(
                            item.target_genders,
                            "Herkes"
                          )}
                        />

                        <Detail
                          label="Sektör hedefleri"
                          value={listValue(
                            item.target_sectors,
                            "Tüm sektörler"
                          )}
                        />

                        <Detail
                          label="Dil hedefleri"
                          value={listValue(
                            item.target_languages,
                            "Tüm diller"
                          )}
                        />

                        <Detail
                          label="İzleme kredi bedeli"
                          value={`${numberValue(
                            item.view_credit_cost
                          )} MRK`}
                        />

                        <Detail
                          label="Tıklama kredi bedeli"
                          value={`${numberValue(
                            item.click_credit_cost
                          )} MRK`}
                        />

                        <Detail
                          label="Vitrin teklif kredisi"
                          value={`${numberValue(
                            item.showcase_bid_credits
                          )} MRK`}
                        />

                        <Detail
                          label="Yayın hakkı onayı"
                          value={
                            item.consent_confirmed
                              ? "Onaylandı"
                              : "Onay bulunmuyor"
                          }
                        />

                        <Detail
                          label="Başlangıç tarihi"
                          value={
                            item.start_at
                              ? dateValue(
                                  item.start_at
                                )
                              : "Onaylandığında"
                          }
                        />

                        <Detail
                          label="Bitiş tarihi"
                          value={
                            item.end_at
                              ? dateValue(
                                  item.end_at
                                )
                              : "Süresiz"
                          }
                        />


                      </div>
                    </div>

                    {!item.consent_confirmed && (
                      <div
                        style={{
                          marginTop: 13,
                          padding: 12,
                          borderRadius: 12,
                          color: "#991b1b",
                          background: "#fee2e2",
                          border:
                            "1px solid #fecaca",
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      >
                        Bu başvuruda yayın hakkı ve
                        reklam kuralları onayı
                        bulunmuyor. Başvuru
                        onaylanmamalıdır.
                      </div>
                    )}

                    {Number(
                      item.view_credit_cost || 0
                    ) <= 0 &&
                      Number(
                        item.click_credit_cost || 0
                      ) <= 0 && (
                        <div
                          style={{
                            marginTop: 13,
                            padding: 12,
                            borderRadius: 12,
                            color: "#92400e",
                            background: "#fef3c7",
                            border:
                              "1px solid #fde68a",
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          İzleme ve tıklama kredi
                          bedeli 0. Onaylanırsa bu
                          reklam ücretsiz
                          yayınlanacaktır.
                        </div>
                      )}

                    {item.description && (
                      <p
                        style={{
                          margin: "13px 0 0",
                          color: COLORS.muted,
                          lineHeight: 1.6,
                        }}
                      >
                        {item.description}
                      </p>
                    )}

                    {item.review_note && (
                      <div
                        style={{
                          marginTop: 13,
                          padding: 12,
                          borderRadius: 12,
                          color: COLORS.navy,
                          background: "#eef4fb",
                          border: "1px solid #cfe0f3",
                        }}
                      >
                        <b>Yönetici notu:</b> {item.review_note}
                      </div>
                    )}

                    {item.status === "pending" && (
                      <div style={{ marginTop: 14 }}>
                        <textarea
                          value={notes[item.id] || ""}
                          onChange={(event) =>
                            setNotes((previous) => ({
                              ...previous,
                              [item.id]: event.target.value,
                            }))
                          }
                          placeholder="Onay notu veya zorunlu ret gerekçesi..."
                          style={{
                            width: "100%",
                            minHeight: 78,
                            resize: "vertical",
                            padding: 12,
                            borderRadius: 12,
                            border: `1px solid ${COLORS.border}`,
                            fontFamily: "inherit",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 9,
                            marginTop: 9,
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => decide(item, "reject")}
                            style={{
                              padding: "10px 14px",
                              borderRadius: 12,
                              border: "1px solid #fecaca",
                              color: COLORS.red,
                              background: "#fff1f2",
                              fontWeight: 900,
                              cursor: "pointer",
                            }}
                          >
                            Reddet
                         


 </button>
                                                <button
                            type="button"
                            disabled={
                              busy ||
                              item.consent_confirmed !==
                                true
                            }
                            onClick={() =>
                              decide(item, "approve")
                            }
                            title={
                              item.consent_confirmed
                                ? "Başvuruyu onayla"
                                : "Yayın hakkı onayı bulunmuyor"
                            }
                            style={{
                              padding: "10px 14px",
                              borderRadius: 12,
                              border: 0,
                              color: "#fff",
                              background:
                                item.consent_confirmed
                                  ? COLORS.green
                                  : "#94a3b8",
                              fontWeight: 900,
                              cursor:
                                busy ||
                                !item.consent_confirmed
                                  ? "not-allowed"
                                  : "pointer",
                              opacity: busy
                                ? 0.65
                                : 1,
                            }}
                          >
                            {busy
                              ? "İşleniyor..."
                              : item.consent_confirmed
                                ? "İncele ve Onayla"
                                : "Yayın Onayı Eksik"}
                          </button>




                        </div>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
